import React, { useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import Modal from '../../../../components/modules/Modal';
import { ContainedButton, OutlinedButton } from '../../../../components/modules/Buttons';
import {
  getOrganizationPublicDownloadLinkExpiry,
  getPublicDownloadLinkExpiry,
  patchOrganizationPublicDownloadLinkExpiry,
  patchPublicDownloadLinkExpiry,
} from '../../../../api/usersApi';
import styles from './PublicDownloadLinkExpiryModal.module.scss';

type TPublicDownloadLinkExpiryModalProps = {
  open: boolean;
  onClose: () => void;
  organizationId?: string;
  organizationName?: string;
};

const OPTIONS = [
  { label: '24 hours', value: 24 * 60 * 60 },
  { label: '7 days', value: 7 * 24 * 60 * 60 },
  { label: '30 days', value: 30 * 24 * 60 * 60 },
];

const PublicDownloadLinkExpiryModal: React.FC<TPublicDownloadLinkExpiryModalProps> = ({
  open,
  onClose,
  organizationId,
  organizationName,
}) => {
  const [loading, setLoading] = useState(false);
  const [ttlSeconds, setTtlSeconds] = useState(7 * 24 * 60 * 60);
  const [scope, setScope] = useState<'server' | 'organization'>('server');

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      try {
        setLoading(true);
        const res = organizationId
          ? await getOrganizationPublicDownloadLinkExpiry(organizationId)
          : await getPublicDownloadLinkExpiry();
        setTtlSeconds(Number(res?.ttlSeconds) || 7 * 24 * 60 * 60);
        setScope(res?.scope === 'organization' ? 'organization' : 'server');
      } catch (error) {
        enqueueSnackbar('Failed to load public download link expiry', {
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [open, organizationId]);

  const onSave = async () => {
    try {
      setLoading(true);
      if (organizationId) {
        await patchOrganizationPublicDownloadLinkExpiry(
          organizationId,
          ttlSeconds,
        );
      } else {
        await patchPublicDownloadLinkExpiry(ttlSeconds);
      }
      enqueueSnackbar('Public download link expiry updated', {
        variant: 'success',
      });
      onClose();
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          'Failed to update public download link expiry',
        {
          variant: 'error',
        },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.title}>
          Public Download Link Expiry{organizationName ? ` - ${organizationName}` : ''}
        </div>
        <div className={styles.subtitle}>
          {scope === 'organization'
          ? 'Default for external-share emails and copied downloadable links in this organisation.'
            : 'Server default for external-share emails and copied downloadable links.'}
        </div>

        <div className={styles.options}>
          {OPTIONS.map((option) => (
            <label className={styles.optionRow} key={option.value}>
              <input
                type="radio"
                checked={ttlSeconds === option.value}
                onChange={() => setTtlSeconds(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>

        <div className={styles.actions}>
          <ContainedButton disabled={loading} onClick={onSave}>
            Save
          </ContainedButton>
          <OutlinedButton disabled={loading} kind="default" onClick={onClose}>
            Cancel
          </OutlinedButton>
        </div>
      </div>
    </Modal>
  );
};

export default PublicDownloadLinkExpiryModal;
